import { barcodeSvg, type Symbology } from '@/lib/printing/barcode'

export interface ProductLabel {
  productId: number
  storeName: string
  name: string
  sku: string
  code: string
  symbology: Symbology
  priceUsd: number
  priceLbp: number
  /** "kg" when the price is per unit of weight, so the label cannot be read as a per-item price. */
  priceUnit: string | null
  widthMm: number
  heightMm: number
  showPrice: boolean
  copies: number
}

/**
 * Renders labels for a roll-fed label printer.
 *
 * One label per page, with `@page` set to the exact roll size. That is what makes a label printer
 * feed correctly: the driver advances one label per page, so a page that is the wrong size either
 * splits a label across two or leaves a gap that drifts further with every print.
 *
 * The barcode is drawn at whole-module widths inside an SVG and scaled by the box around it,
 * never by resampling the bars themselves — a scanner reading one module as two gets a different
 * number, and it will not tell you it did.
 */
export function renderLabelsHtml(labels: ProductLabel[]): string {
  if (labels.length === 0) {
    return '<!doctype html><html><body></body></html>'
  }

  const { widthMm, heightMm } = labels[0]!

  // Copies are expanded here rather than by the print dialog, so "20 of this one and 5 of that"
  // comes out in one pass in the right order.
  const pages = labels
    .flatMap((label) => Array.from({ length: label.copies }, () => label))
    .map((label) => page(label))
    .join('')

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Labels</title>
<style>
  @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #000; background: #fff; }

  .label {
    width: ${widthMm}mm;
    height: ${heightMm}mm;
    padding: 1.5mm;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
    /* Every label starts a new page; the last must not add a blank one. */
    page-break-after: always;
    break-after: page;
  }
  .label:last-child { page-break-after: auto; break-after: auto; }

  .store { font-size: 5pt; text-transform: uppercase; letter-spacing: 0.3pt; }
  .name {
    font-size: 8pt;
    font-weight: 700;
    line-height: 1.1;
    /* Two lines at most: a long name must not push the barcode off the label. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .price { font-size: 12pt; font-weight: 700; line-height: 1; }
  .price .unit { font-size: 7pt; font-weight: 400; }
  .lira { font-size: 6pt; }
  .code { flex: 1; min-height: 0; margin-top: 0.5mm; }
  .row { display: flex; align-items: flex-end; justify-content: space-between; gap: 1mm; }
</style>
</head>
<body>${pages}</body>
</html>`
}

function page(label: ProductLabel): string {
  const price = label.showPrice
    ? `<div class="row">
         <div>
           <div class="price">$${label.priceUsd.toFixed(2)}${
             // Said on the label, because "$4.00" on a bag of charcoal priced per kilo is a
             // different claim from "$4.00 per kilo" and customers hold you to the label.
             label.priceUnit ? `<span class="unit">/${escape(label.priceUnit)}</span>` : ''
           }</div>
           ${label.priceLbp > 0 ? `<div class="lira">${label.priceLbp.toLocaleString()} LL</div>` : ''}
         </div>
       </div>`
    : ''

  return `<div class="label">
    <div>
      <div class="store">${escape(label.storeName)}</div>
      <div class="name">${escape(label.name)}</div>
    </div>
    ${price}
    <div class="code">${barcodeSvg(label.code, label.symbology)}</div>
  </div>`
}

function escape(value: string): string {
  return value.replace(
    /[&<>"]/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[character] ?? character,
  )
}
